import {
    useContext,
    useState,
    useRef
} from "react";
import {
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    LocaleSwitcher,
    ThemeSwitcher,
    PageContainer,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../navigation/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    HomeIcon
} from "lucide-react-native";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Login = () => {
    const [
        identifier,
        setIdentifier
    ] = useState("");

    const [
        password,
        setPassword
    ] = useState("");

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const passwordRef = useRef<import("ncore-ui-kit").ITextInputRef>(null);

    const {
        guestLogin,
        login
    } = useContext(AuthContext);

    const {
        activeTheme,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const handleLogin = async () => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch(`${process.env.API_URL}/auth/login`, {
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    identifier,
                    password
                }),
                method: "POST"
            });

            const data = await response.json();

            if (data.status === "success") {
                await login(data.token, data.user);
            } else {
                NCoreUIKitToast.open({
                    title: data.message || localize("login-toast-failed")
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("login-toast-network-error")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return <PageContainer
        scrollViewProps={{
            showsHorizontalScrollIndicator: false,
            showsVerticalScrollIndicator: false,
            contentContainerStyle: {
                flexGrow: 1
            }
        }}
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <Image
                source={{
                    uri: activeTheme.indexOf("dark") !== -1 ? "https://dergah.app/assets/images/darklogo.png" : "https://dergah.app/assets/images/lightlogo.png"
                }}
                style={[
                    stylesheet.logo,
                    {
                        marginBottom: spaces.spacingXl
                    }
                ]}
                resizeMode="contain"
            />
            <Text
                variant="titleLargeSize"
            >
                {localize("login-page-title")}
            </Text>
            <Text
                style={{
                    marginBottom: spaces.spacingXl,
                    marginTop: spaces.spacingMd
                }}
                variant="bodyLargeSize"
            >
                {localize("login-page-subtitle")}
            </Text>
            <View
                style={stylesheet.formContainer}
            >
                <TextInput
                    onSubmitEditing={() => passwordRef.current?.focus?.()}
                    placeholder={localize("login-identifier-placeholder")}
                    keyboardType="email-address"
                    onChangeText={setIdentifier}
                    spreadBehaviour="stretch"
                    autoCapitalize="none"
                    returnKeyType="next"
                    value={identifier}
                />
                <View
                    style={{
                        height: spaces.spacingMd
                    }}
                />
                <TextInput
                    placeholder={localize("login-password-placeholder")}
                    onSubmitEditing={handleLogin}
                    onChangeText={setPassword}
                    spreadBehaviour="stretch"
                    returnKeyType="done"
                    ref={passwordRef}
                    variant="hidden"
                    value={password}
                />
                <View
                    style={{
                        height: spaces.spacingLg
                    }}
                />
                <Button
                    title={localize("login-submit-button")}
                    spreadBehaviour="stretch"
                    isLoading={isSubmitting}
                    onPress={handleLogin}
                />
                <View
                    style={[
                        stylesheet.forgotPasswordContainer,
                        {
                            marginTop: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        onPress={() => navigation.navigate("ForgotPassword")}
                        title={localize("login-forgot-password-button")}
                        spreadBehaviour="stretch"
                        variant="ghost"
                    />
                </View>
                <View
                    style={[
                        stylesheet.guestButtonContainer,
                        {
                            marginTop: spaces.spacingSm
                        }
                    ]}
                >
                    <Button
                        title={localize("login-guest-button")}
                        spreadBehaviour="stretch"
                        onPress={guestLogin}
                        variant="outline"
                    />
                </View>
            </View>
        </View>
        <View
            style={[
                stylesheet.switchersContainer,
                {
                    left: spaces.spacingXl,
                    top: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                onPress={() => navigation.navigate("Landing")}
                icon={({
                    color,
                    size
                }) => {
                    return <HomeIcon
                        color={colors.content.icon[color]}
                        size={size + 6}
                    />;
                }}
                variant="filled"
                type="neutral"
            />
            <LocaleSwitcher />
            <ThemeSwitcher />
        </View>
    </PageContainer>;
};
export default Login;
